import * as _ from 'lodash';
import { DeviceInformation } from '../models/device-information';
import { DynamicControlsSharedStateService } from './dynamic-controls-shared-state.service';
import { DeviceCategoryVisitor } from '../../visitors/device-category-visitor';
import { DeviceInputVisitor } from '../../visitors/device-input-visitor';
import { DeviceSectionVisitor } from '../../visitors/device-section-visitor';
import { DynamicCalendarTabVisitor } from '../../visitors/dynamic-calendar-tab-visitor';
import { DynamicCalendarVisitor } from '../../visitors/dynamic-calendar-visitor';
import { DynamicListInputVisitor } from '../../visitors/dynamic-list-input-visitor';
import { IsEnabledVisitor } from '../../visitors/is-enabled-visitor';
import { SelectListInputVisitor } from '../../visitors/selected-list-input-visitor';
import { StaticListInputVisitor } from '../../visitors/static-list-input-visitor';
import { StaticListTabVisitor } from '../../visitors/static-list-tab-visitor';

import { Injectable } from '@angular/core';
import { InputMapService } from './input-map.service';
import {
    IControlInput,
    IDeviceCategory,
    IDeviceInformation,
    IDeviceItemGrouping,
    IDeviceItemVisitor,
    IDeviceNode
} from '../models/device';
import { FlatDeviceInputVisitor } from '../../visitors/flat-device-input-visitor';
import {
    CommandOptimizationRenderingService
} from '@caslink/device-client/ng-app/shared/services/command-optimization-rendering.service';

// Loads the device hierarchy and process it by applying a collection
// of visitors.  Also provides services for submitting the structure
// back to the server.
@Injectable({
    providedIn: 'root'
})
export class DeviceService {
    public static $name = 'deviceService';

    private _visitors: IDeviceItemVisitor[];

    constructor(
        private dynamicControlsSharedStateFactory: DynamicControlsSharedStateService,
        private inputMapService: InputMapService,
        private commandOptimizationRenderingService: CommandOptimizationRenderingService,
    ) {
    }

    public setControlInputType(controlInput: IControlInput) {
        if (controlInput.fieldRelationship) {
            controlInput.$inputType = controlInput.fieldRelationship.relationshipType;
        } else {
            let inputType = controlInput.elementType;
            let inputMap = this.inputMapService.getNumberInputMapping(inputType);

            controlInput.$inputType = (inputMap && inputMap.inputType) || inputType;
            controlInput.$inputTypeMap = inputMap;
        }
    }

    public setupDeviceInfo(node: IDeviceNode) {
        let deviceInfo = new DeviceInformation(node);
        this.initVisitors(node, deviceInfo);
        this.visitDeviceStructure(node, deviceInfo);
        this.determineVisibility(deviceInfo);
        return deviceInfo;
    }

    public visitDeviceStructure(node: IDeviceNode, currGrouping: IDeviceItemGrouping) {
        if (DeviceService.isNodeToBeVisited(node)) {
            this.visitNode(node, currGrouping);
        } else if (_.isArray(node)) {
            this.visitNodeArray(node, currGrouping);
        }
    }

    /**
     * After visiting all of the nodes in the deviceInfo, we need to separately determine if we can display things or not.
     * This is because a tab may initially appear to be visible but have no visible items under it.
     * @param deviceInfo The already-visited device information.
     */
    private determineVisibility(deviceInfo: DeviceInformation) {
        if (!deviceInfo.$categories) {
            return;
        }

        /* The top level contains the category tabs */
        deviceInfo.$categories.forEach(($category: IDeviceCategory) => {
            /* If the category was already hidden, don't bother */
            if (!$category.isVisible) {
                return;
            }

            $category.isVisible = this.canShowGrouping($category);
        });
    }

    private canShowGrouping(grouping: IDeviceItemGrouping) {
        return this.hasVisibleInputs(grouping.$controlInputs) || this.hasVisibleSections(grouping.$sections);
    }

    private hasVisibleInputs(controlInputs: IControlInput[]) {
        return controlInputs.some((controlInput: IControlInput) => {
            if (controlInput.isVisible) {
                return true;
            }

            return this.hasVisibleSections(controlInput.$sections);
        });
    }

    /* It is possible for a $controlInput to have isVisible = false but still have sections underneath it that can be displayed & edited.
     * Example: Fan Zones may have the number input to determine
     * how many fan zones disabled/locked, but still have the existing zones editable
     *
     * It's also possible for a DeviceCategory to only have a DeviceSection visible & no other inputs.
     */
    private hasVisibleSections(sections: IDeviceItemGrouping[]) {
        return sections && sections.length > 0 && sections.some((section: IDeviceItemGrouping) => {
            /* Recursively dive into the sections --
             * we need to make sure that the section itself has at
             * least one visible input OR a subsection with at least one visible input */
            return this.canShowGrouping(section);
        });
    }

    // -----------------------------------------------------------------------------
    // Visitor Implementation.
    // -----------------------------------------------------------------------------

    // Populate and initialize the list of visitors that will visit the object
    // model and build a grouping of categories, sections, and device inputs.
    private initVisitors(node: IDeviceNode, device: IDeviceInformation) {
        let sharedState = this.dynamicControlsSharedStateFactory.create();

        // Note:  Order is important for some visitors.
        this._visitors = [
            // This needs to come first so we can handle the related inputs in isolation
            new DynamicListInputVisitor(),

            // Tabs
            new DeviceCategoryVisitor(),
            new DynamicCalendarTabVisitor(),
            new StaticListTabVisitor(),

            new DeviceSectionVisitor(),

            new SelectListInputVisitor(),
            new StaticListInputVisitor(),

            new DynamicCalendarVisitor(),

            // DeviceInputVisitor needs to come last (out of the element type visitors) --
            // it is the fallback which others will become if they did not fit any other visitor
            new DeviceInputVisitor(this.commandOptimizationRenderingService),

            new FlatDeviceInputVisitor(this.commandOptimizationRenderingService),

            new IsEnabledVisitor()
        ];

        // Allow each visitor initialize itself.
        _.forEach(this._visitors, (v: IDeviceItemVisitor) => {
            v.service = this;
            v.init(node, device, sharedState);
        });
    }

    private static isNodeToBeVisited(node: IDeviceNode): boolean {
        return typeof node === 'object';
    }

    private visitNode(node: IDeviceNode, currGrouping: IDeviceItemGrouping) {
        let propsToVisit = this.getPropsToVisit(node);

        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < propsToVisit.length; i++) {
            let propName = propsToVisit[i];
            let propValue = node[propName];

            this.visitProperty(node, propName, propValue, currGrouping);
        }
    }

    private getPropsToVisit(node: IDeviceNode): string[] {
        let propsToVisit: string[] = Object.keys(node).filter(key => {
            let propName = key;

            if (!node.hasOwnProperty(propName)) {
                return false;
            }

            let propValue = node[propName];

            if (DeviceService.isPropertyToBeVisited(propName) && DeviceService.isNodeToBeVisited(propValue)) {
                return true;
            }
        });

        return propsToVisit.sort((a, b) => {
            let current = node[a].order;

            // Those without order should be pushed to the end
            if (!(typeof current !== 'undefined')) {
                return 1;
            }

            let next = node[b].order;

            // Those with matching orders should remain in the same order the came in.
            // Those with lesser orders should be pushed to the front
            return +current - +next;
        });
    }

    private static isPropertyToBeVisited(propName: string) {
        // tslint:disable-next-line: triple-equals
        return propName[0] != '$' && propName != 'selectLists';
    }

    private visitNodeArray(nodes: any, currGrouping: IDeviceItemGrouping) {
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < nodes.length; i++) {
            let node = nodes[i];
            this.visitNode(node, currGrouping);
        }
    }

    private visitProperty(owningObject: IDeviceNode, propName: string, propValue: IDeviceNode, currGrouping: IDeviceItemGrouping) {
        let grouping: IDeviceItemGrouping = null;

        // Invoke all visitors that indicated they can visit node.
        for (let visitor of this._visitors) {
            if (visitor.canVisit(owningObject, propName, propValue)) {
                visitor.currentGrouping = currGrouping;
                grouping = visitor.visit(owningObject, propName, propValue);

                // Determine if the visitor returned a new grouping
                // and if so, make it the current grouping.
                if (grouping != null && grouping !== currGrouping) {
                    currGrouping = grouping;
                }
            }
        }

        // Now, visit the property's value if a visitor didn't indicate that
        // no further processing is needed.
        if (!propValue.$ignoreChildren) {
            this.visitNode(propValue, currGrouping);
        }
    }
}
